import React from 'react';
import { toNumber } from 'lodash-es';
import { useDispatch, useSelector } from 'react-redux';
import { CampaignIcon } from '@dev/ee-components';
import { Trans, useTranslation } from 'react-i18next';
import { push } from 'redux-first-history';
import { APIPaths, SettingsPaths } from '@/services/dictionaries/Paths';
import { SaveOnboardingStart, getOnboardingStore } from '@/store/onboarding/actions';
import { Link } from '@material-ui/core';
import { links } from '@/services/dictionaries/links';
import { OnboardingVideoThumbnail } from '@/components/Onboarding/components/OnboardingVideoThumbnail';
import { YT_GENERATE_API_KEY_ID, YT_GENERATE_SMTP_KEY } from '@/services/constants';
import { OnboardingTask, OnboardingSteps, OnboardingTaskProps } from '../../../types';
import SvgApiDocumentation from './svgs/svg-api-documentation.svg';
import SvgSmtpApiReport from './svgs/svg-smtp-api-report.svg';
import SvgDocumentation from '../../../svgs/customicon-documentation.svg';
import SvgLibrary from '../../../svgs/customicon-library.svg';

const taskStepsId1 = 'sendTransactionalEmailsTaskSteps1';
const taskStepsId2 = 'sendTransactionalEmailTaskSteps2';

export enum SendTransationalEmailsTaskSavedDataKeysEnum {
    SendTransactionalEmailsSmtpActive = 'SendTransactionalEmailsSmtpActive',
    SendTransactionalEmailsSmtpConnectWithTool = 'SendTransactionalEmailsSmtpConnectWithTool',
    SendTransactionalEmailsSmtpViewLogs = 'SendTransactionalEmailsSmtpViewLogs',
    SendTransactionalEmailsSkipSmtpStep1 = 'SendTransactionalEmailsSkipSmtpStep1',
    SendTransactionalEmailsSkipSmtpStep2 = 'SendTransactionalEmailsSkipSmtpStep2',
    SendTransactionalEmailsSkipSmtpStep3 = 'SendTransactionalEmailsSkipSmtpStep3',

    SendTransactionalEmailsApiActive = 'SendTransactionalEmailsApiActive',
    SendTransactionalEmailsApiConnectWithTool = 'SendTransactionalEmailsApiConnectWithTool',
    SendTransactionalEmailsApiViewLogs = 'SendTransactionalEmailsApiViewLogs',
    SendTransactionalEmailsSkipApiStep1 = 'SendTransactionalEmailsSkipApiStep1',
    SendTransactionalEmailsSkipApiStep2 = 'SendTransactionalEmailsSkipApiStep2',
    SendTransactionalEmailsSkipApiStep3 = 'SendTransactionalEmailsSkipApiStep3',
}
// eslint-disable-next-line max-lines-per-function
export const useSendTransactionalEmailsTask = ({ onboardingGoals }: OnboardingTaskProps): OnboardingTask => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { savedData, data } = useSelector(getOnboardingStore);
    const activeStepsIndex = toNumber(!!savedData?.SendTransactionalEmailsApiActive);

    const saveSmtpAsActive = () => {
        dispatch(
            SaveOnboardingStart({ SendTransactionalEmailsSmtpActive: true, SendTransactionalEmailsApiActive: false })
        );
    };
    const saveApiAsActive = () => {
        dispatch(
            SaveOnboardingStart({ SendTransactionalEmailsSmtpActive: false, SendTransactionalEmailsApiActive: true })
        );
    };
    const handleGoToCreateSMTP = () => {
        dispatch(push(SettingsPaths.CreateSMTP));
    };
    const handleGoToCreateAPIKey = () => {
        dispatch(push(SettingsPaths.CreateAPI));
    };
    const handleConnectWithToolSMTPDone = () => {
        dispatch(SaveOnboardingStart({ SendTransactionalEmailsSmtpConnectWithTool: true }));
    };
    const handleConnectWithToolAPIDone = () => {
        dispatch(SaveOnboardingStart({ SendTransactionalEmailsApiConnectWithTool: true }));
    };
    const handleCheckLogsSMTP = () => {
        dispatch(SaveOnboardingStart({ SendTransactionalEmailsSmtpViewLogs: true }));
        dispatch(push(APIPaths.LogsEmail));
    };
    const handleCheckLogsAPI = () => {
        dispatch(SaveOnboardingStart({ SendTransactionalEmailsApiViewLogs: true }));
        dispatch(push(APIPaths.LogsEmail));
    };

    const sendEmailSteps: OnboardingSteps[] = [
        {
            id: taskStepsId1,
            title: t('iwanttosendviasmtp'),
            saveAsActive: saveSmtpAsActive,
            items: [
                {
                    title: t('createsmtpcredentials'),
                    features: [
                        t('sendemailsviasmtp_createsmtpcredentials_feature1'),
                        <Trans
                            i18nKey="sendemailsviasmtp_createsmtpcredentials_feature2"
                            components={{
                                url: <Link onClick={() => dispatch(push(SettingsPaths.Domains))} />,
                            }}
                            key="sendemailsviasmtp_createsmtpcredentials_feature2"
                        />,
                    ],
                    secondaryContent: <OnboardingVideoThumbnail videoId={YT_GENERATE_SMTP_KEY} />,
                    primaryActionTitle: t('createsmtpcredentials'),
                    primaryActionCallback: handleGoToCreateSMTP,
                    isSkipped: !!savedData?.SendTransactionalEmailsSkipSmtpStep1,
                    skipSavedDataKey: SendTransationalEmailsTaskSavedDataKeysEnum.SendTransactionalEmailsSkipSmtpStep1,
                    isCompleted: data?.OneSmtp,
                },
                {
                    title: t('connectwithyourtool'),
                    features: [
                        t('sendemailsviasmtp_connectwithyourtool_feature1'),
                        <Trans
                            i18nKey="sendemailsviasmtp_connectwithyourtool_feature2"
                            components={{
                                url: <Link href={links.integrations} target="_blank" />,
                            }}
                            key="sendemailsviasmtp_connectwithyourtool_feature2"
                        />,
                    ],
                    secondaryContent: <SvgApiDocumentation />,
                    primaryActionTitle: t('markasdone'),
                    primaryActionCallback: handleConnectWithToolSMTPDone,
                    isNotReady: !!savedData?.SendTransactionalEmailsSkipSmtpStep1 || !data?.OneSmtp,
                    isSkipped: !!savedData?.SendTransactionalEmailsSkipSmtpStep2,
                    skipSavedDataKey: SendTransationalEmailsTaskSavedDataKeysEnum.SendTransactionalEmailsSkipSmtpStep2,
                    isCompleted: !!savedData?.SendTransactionalEmailsSmtpConnectWithTool,
                },
                {
                    title: t('checklogs'),
                    features: [
                        t('sendemailsviasmtp_checklogs_feature1'),
                        <Trans
                            i18nKey="sendemailsviasmtp_checklogs_feature2"
                            components={{
                                url: <Link href={links.whatisemaildeliverystatus} target="_blank" />,
                            }}
                            key="sendemailsviasmtp_checklogs_feature2"
                        />,
                    ],
                    secondaryContent: <SvgSmtpApiReport />,
                    primaryActionTitle: t('viewemaillogs'),
                    primaryActionCallback: handleCheckLogsSMTP,
                    isNotReady:
                        !!savedData?.SendTransactionalEmailsSkipSmtpStep1 ||
                        !!savedData?.SendTransactionalEmailsSkipSmtpStep2 ||
                        !data?.OneSmtp,
                    isSkipped: !!savedData?.SendTransactionalEmailsSkipSmtpStep3,
                    skipSavedDataKey: SendTransationalEmailsTaskSavedDataKeysEnum.SendTransactionalEmailsSkipSmtpStep3,
                    isCompleted: !!savedData?.SendTransactionalEmailsSmtpViewLogs,
                },
            ],
        },
        {
            id: taskStepsId2,
            title: t('iwanttosendviaapi'),
            saveAsActive: saveApiAsActive,
            items: [
                {
                    title: t('createapikey'),
                    features: [
                        t('sendemailsviaapi_createapikey_feature1'),
                        t('sendemailsviaapi_createapikey_feature2'),
                        <Trans
                            i18nKey="sendemailsviaapi_createapikey_feature3"
                            components={{
                                url: <Link onClick={() => dispatch(push(SettingsPaths.Domains))} />,
                            }}
                            key="sendemailsviaapi_createapikey_feature3"
                        />,
                    ],
                    secondaryContent: <OnboardingVideoThumbnail videoId={YT_GENERATE_API_KEY_ID} />,
                    primaryActionTitle: t('createapikey'),
                    primaryActionCallback: handleGoToCreateAPIKey,
                    isSkipped: !!savedData?.SendTransactionalEmailsSkipApiStep1,
                    skipSavedDataKey: SendTransationalEmailsTaskSavedDataKeysEnum.SendTransactionalEmailsSkipApiStep1,
                    isCompleted: data?.OneApiKey,
                },
                {
                    title: t('connectwithyourtool'),
                    features: [
                        t('sendemailsviaapi_connectwithyourtool_feature1'),
                        t('sendemailsviaapi_connectwithyourtool_feature2'),
                    ],
                    links: [
                        {
                            href: links.restApiDoc,
                            label: t('gotoapidocumentation'),
                            icon: <SvgDocumentation />,
                            target: '_blank',
                        },
                        {
                            href: links.apiLibraries,
                            label: t('gotoapilibrary'),
                            icon: <SvgLibrary />,
                            target: '_blank',
                        },
                    ],
                    secondaryContent: <SvgApiDocumentation />,
                    primaryActionTitle: t('markasdone'),
                    primaryActionCallback: handleConnectWithToolAPIDone,
                    isNotReady: !!savedData?.SendTransactionalEmailsSkipApiStep1 || !data?.OneApiKey,
                    isSkipped: !!savedData?.SendTransactionalEmailsSkipApiStep2,
                    skipSavedDataKey: SendTransationalEmailsTaskSavedDataKeysEnum.SendTransactionalEmailsSkipApiStep2,
                    isCompleted: !!savedData?.SendTransactionalEmailsApiConnectWithTool,
                },
                {
                    title: t('checklogs'),
                    features: [
                        t('sendemailsviasmtp_checklogs_feature1'),
                        <Trans
                            i18nKey="sendemailsviasmtp_checklogs_feature2"
                            components={{
                                url: <Link href={links.whatisemaildeliverystatus} target="_blank" />,
                            }}
                            key="sendemailsviasmtp_checklogs_feature2"
                        />,
                    ],
                    secondaryContent: <SvgSmtpApiReport />,
                    primaryActionTitle: t('viewreport'),
                    primaryActionCallback: handleCheckLogsAPI,
                    isNotReady:
                        !!savedData?.SendTransactionalEmailsSkipApiStep1 ||
                        !!savedData?.SendTransactionalEmailsSkipApiStep2 ||
                        !data?.OneApiKey,
                    isSkipped: !!savedData?.SendTransactionalEmailsSkipApiStep3,
                    skipSavedDataKey: SendTransationalEmailsTaskSavedDataKeysEnum.SendTransactionalEmailsSkipApiStep3,
                    isCompleted: !!savedData?.SendTransactionalEmailsApiViewLogs,
                },
            ],
        },
    ];

    return {
        header: t('sendtransactionalemails'),
        icon: <CampaignIcon />,
        title: t('delivertransactionalemailswithease'),
        description: (
            <Trans
                i18nKey="whendecidingwhether"
                components={{ url: <Link href={links.apiVsSmtp} target="_blank" /> }}
            />
        ),
        steps: sendEmailSteps,
        isVisible:
            onboardingGoals.isSendingTransactionalEmailsAnswered &&
            !(onboardingGoals.isConnectingViaAPIAnswered || onboardingGoals.isConnectingViaSMTPAnswered),
        activeStepsIndex,
    };
};
